import React, { useEffect, useState } from "react";
import { AnimatePresence } from "framer-motion";
import Transition from "../../../../../components/Transition";
import CardLoader from "../../../../../components/CardLoader";
import Toast from "../../../../../components/Toast";
import ButtonLoader from "../../../../../components/ButtonLoader";
import Pagination from "../../../../../components/Pagination";
import { BrokersData } from "../network/api/BrokersData";

const normalizeText = (value) => (value === null || value === undefined ? "" : String(value));

const normalizeServers = (broker) => {
    if (Array.isArray(broker?.servers) && broker.servers.length > 0) {
        return broker.servers;
    }

    if (broker?.server || broker?.terminal_url) {
        return [{
            id: broker?.id,
            server: broker?.server || "",
            terminal_url: broker?.terminal_url || "",
        }];
    }

    return [];
};

const getServerCount = (broker) => {
    if (typeof broker?.server_count === "number") {
        return broker.server_count;
    }

    return normalizeServers(broker).length;
};

export default function BrokerList({
    reloadKey,
    setReloadKey,
    setEditMode,
    setSelectedBroker,
    selectedBroker,
    canManageBrokers = false,
}) {
    const [brokers, setBrokers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [searchValue, setSearchValue] = useState("");
    const [toastConfig, setToastConfig] = useState({
        message: "",
        type: "primary",
        visible: false,
    });
    const [pageProps, setPageProps] = useState({
        page: 1,
        totalPages: 1,
        totalEntries: 0,
        entryLimit: 10,
        visiblePages: [1],
    });
    const [pageFilters, setPageFilters] = useState({
        page: null,
        limit: 10,
        q: null,
    });

    const handlePageChange = (page) => {
        if (page >= 1 && page <= pageProps.totalPages) {
            setPageProps((prev) => ({ ...prev, page }));
            setPageFilters((prev) => ({ ...prev, page }));
        }
    };

    const handleLimitChange = (value) => {
        setPageFilters((prev) => ({
            ...prev,
            page: null,
            limit: value,
        }));
    };

    const handleSearchChange = (event) => {
        const value = event.target.value;
        setSearchValue(value);

        if (value.length >= 3) {
            setPageFilters((prev) => ({
                ...prev,
                page: null,
                q: value,
            }));
        }

        if (value.length === 0) {
            setPageFilters((prev) => ({
                ...prev,
                page: null,
                q: null,
            }));
        }
    };

    const handleCreateBroker = (event) => {
        event.preventDefault();
        setSelectedBroker(null);
        setEditMode(true);
    };

    const handleEditBroker = (event, broker) => {
        event.preventDefault();
        setSelectedBroker(broker);
        setEditMode(true);
    };

    const handleDeleteBroker = async () => {
        if (!canManageBrokers) {
            setToastConfig({
                message: "Trusted super admin access is required to delete brokers.",
                type: "danger",
                visible: true,
            });
            return;
        }

        setIsLoading(true);
        const response = await BrokersData({
            method: "DELETE",
            id: selectedBroker?.id,
        });
        setIsLoading(false);
        setConfirmDelete(false);

        if (response.status === "success") {
            document.querySelector("#deleteBrokerModal .btn-close")?.click();
            setToastConfig({
                message: response.message || "Broker deleted successfully.",
                type: "success",
                visible: true,
            });
            setReloadKey((prev) => !prev);
            setSelectedBroker(null);
        } else {
            setToastConfig({
                message: response.message || "Unable to delete broker.",
                type: "danger",
                visible: true,
            });
        }
    };

    useEffect(() => {
        const fetchBrokers = async () => {
            if (!canManageBrokers) {
                return;
            }

            setIsLoading(true);
            setErrorMessage(null);

            const response = await BrokersData({
                method: "GET",
                query: pageFilters,
            });

            if (response.status === "success" && response.data) {
                const payload = response.data && typeof response.data === "object" ? response.data : {};
                const rows = Array.isArray(payload.data) ? payload.data : Array.isArray(response.data) ? response.data : [];

                setBrokers(rows);
                setPageProps({
                    page: payload.page || pageFilters.page || 1,
                    totalPages: payload.total_pages || 1,
                    totalEntries: payload.total_records || rows.length || 0,
                    entryLimit: payload.limit || pageFilters.limit || 10,
                    visiblePages: payload.visible_pages || [1],
                });
            } else {
                setErrorMessage({
                    code: response.code,
                    message: response.message || "Failed to load brokers.",
                });
            }

            setIsLoading(false);
        };

        fetchBrokers();
    }, [canManageBrokers, pageFilters, reloadKey]);

    return (
        <>
            <AnimatePresence mode="wait">
                <Transition key="broker-list-page">
                    <Toast
                        message={toastConfig.message}
                        type={toastConfig.type}
                        visible={toastConfig.visible}
                        setVisible={(visible) => setToastConfig({ ...toastConfig, visible })}
                    />

                    <div className="card custom-card">
                        {(isLoading || errorMessage) && (
                            <CardLoader errorMessage={errorMessage} onRetry={() => setReloadKey((prev) => !prev)} />
                        )}

                        <div className="card-header">
                            <div className="d-flex justify-content-between align-items-center w-100 flex-wrap gap-2">
                                <div>
                                    <h4 className="card-title mb-1">Brokers</h4>
                                    <p className="text-muted mb-0 small">One broker can now hold many servers.</p>
                                </div>
                                <button
                                    className="btn btn-primary btn-sm"
                                    onClick={handleCreateBroker}
                                    disabled={!canManageBrokers}
                                >
                                    <i className="fa fa-plus"></i> Add Broker
                                </button>
                            </div>
                        </div>

                        <div className="card-body">
                            {!canManageBrokers && (
                                <div className="alert alert-warning alert-alt solid">
                                    Trusted super admin access is required to create, update, or delete brokers.
                                </div>
                            )}

                            <div className="table-responsive dataTables_wrapper no-footer">
                                <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3">
                                    <label className="mb-0">
                                        Show{" "}
                                        <select
                                            value={pageProps.entryLimit}
                                            onChange={(event) => handleLimitChange(Number(event.target.value))}
                                        >
                                            <option value="10">10</option>
                                            <option value="25">25</option>
                                            <option value="50">50</option>
                                            <option value="100">100</option>
                                        </select>{" "}
                                        entries
                                    </label>

                                    <label className="mb-0">
                                        Search:
                                        <input
                                            type="search"
                                            className="ms-2"
                                            placeholder="Name, server, URL..."
                                            value={searchValue}
                                            onChange={handleSearchChange}
                                        />
                                    </label>
                                </div>

                                <table className="table table-striped align-middle" style={{ minWidth: "1140px" }}>
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Name</th>
                                            <th>Servers</th>
                                            <th>IB URL</th>
                                            <th>Description</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {brokers.map((broker, index) => {
                                            const servers = normalizeServers(broker);

                                            return (
                                                <tr key={broker.id}>
                                                    <td>{(pageProps.entryLimit * (pageProps.page - 1)) + (index + 1)}</td>
                                                    <td>
                                                        <div className="d-flex align-items-center gap-3">
                                                            {broker.image_url ? (
                                                                
                                                                <img
                                                                    src={broker.image_url}
                                                                    alt={broker.name}
                                                                    style={{
                                                                        width: "48px",
                                                                        height: "48px",
                                                                        objectFit: "cover",
                                                                        borderRadius: "10px",
                                                                        border: "1px solid rgba(0,0,0,0.08)",
                                                                    }}
                                                                />
                                                                
                                                            ) : (
                                                                <div
                                                                    className="d-flex align-items-center justify-content-center bg-light rounded"
                                                                    style={{
                                                                        width: "48px",
                                                                        height: "48px",
                                                                        border: "1px solid rgba(0,0,0,0.08)",
                                                                    }}
                                                                >
                                                                    <i className="fa fa-building text-muted"></i>
                                                                </div>
                                                            )}
                                                            <strong>{normalizeText(broker.name)}</strong>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex flex-column gap-2">
                                                            <span className="badge badge-primary align-self-start">
                                                                {getServerCount(broker)} server{getServerCount(broker) === 1 ? "" : "s"}
                                                            </span>

                                                            <details className="small">
                                                                <summary className="text-primary" style={{ cursor: "pointer" }}>
                                                                    View server list
                                                                </summary>
                                                                <div className="mt-2">
                                                                    {servers.length > 0 ? (
                                                                        <ul className="list-unstyled mb-0">
                                                                            {servers.map((server, serverIndex) => (
                                                                                <li
                                                                                    key={server.id || `${server.server}-${serverIndex}`}
                                                                                    className="border rounded p-2 mb-2"
                                                                                >
                                                                                    <div className="fw-semibold">
                                                                                        {normalizeText(server.server) || "Unnamed server"}
                                                                                    </div>
                                                                                    <div className="text-break">
                                                                                        <a href={server.terminal_url} target="_blank" rel="noreferrer">
                                                                                            {normalizeText(server.terminal_url)}
                                                                                        </a>
                                                                                    </div>
                                                                                </li>
                                                                            ))}
                                                                        </ul>
                                                                    ) : (
                                                                        <span className="text-muted">No servers attached.</span>
                                                                    )}
                                                                </div>
                                                            </details>
                                                        </div>
                                                    </td>
                                                    <td className="text-break">
                                                        {broker.broker_ib ? (
                                                            <a href={broker.broker_ib} target="_blank" rel="noreferrer">
                                                                {normalizeText(broker.broker_ib)}
                                                            </a>
                                                        ) : (
                                                            "-"
                                                        )}
                                                    </td>
                                                    <td className="text-break">{normalizeText(broker.description) || "-"}</td>
                                                    <td>
                                                        <div className="d-flex gap-2">
                                                            <button
                                                                className="btn btn-primary shadow btn-xs sharp"
                                                                onClick={(event) => handleEditBroker(event, broker)}
                                                                disabled={!canManageBrokers}
                                                            >
                                                                <i className="fa fa-pencil-alt"></i>
                                                            </button>
                                                            <button
                                                                onClick={() => setSelectedBroker(broker)}
                                                                data-bs-target="#deleteBrokerModal"
                                                                data-bs-toggle="modal"
                                                                className="btn btn-danger shadow btn-xs sharp"
                                                                disabled={!canManageBrokers}
                                                            >
                                                                <i className="fa fa-trash"></i>
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        {brokers.length === 0 && !isLoading ? (
                                            <tr>
                                                <td colSpan="7" className="text-center text-muted py-4">
                                                    No brokers found.
                                                </td>
                                            </tr>
                                        ) : null}
                                    </tbody>
                                </table>
                                <Pagination pageProps={pageProps} onPageChange={handlePageChange} />
                            </div>
                        </div>
                    </div>
                </Transition>
            </AnimatePresence>

            <div className="modal fade" id="deleteBrokerModal">
                <div className="modal-dialog modal-dialog-centered" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Delete Broker</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal"></button>
                        </div>
                        <div className="modal-body">
                            <h6>
                                Are you sure you want to delete <strong>{selectedBroker?.name}</strong>?
                            </h6>
                            <p>This will permanently remove the broker and all of its servers.</p>
                            <div className="form-check mt-3">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="confirmDeleteCheck"
                                    checked={confirmDelete}
                                    onChange={(event) => setConfirmDelete(event.target.checked)}
                                />
                                <label className="form-check-label" htmlFor="confirmDeleteCheck">
                                    I understand this action cannot be undone.
                                </label>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-dark light" data-bs-dismiss="modal">
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-danger"
                                disabled={!confirmDelete || isLoading}
                                onClick={handleDeleteBroker}
                            >
                                {isLoading ? <ButtonLoader /> : "Delete"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
